import { useMemo } from "react";
import { BarChart, Bar, LineChart, Line, PieChart, Pie, Cell, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { useCAPartner } from "@/contexts/CAPartnerContext";

const CHART_COLORS = {
  accent: "hsl(var(--accent))",
  success: "hsl(142 71% 45%)",
  warning: "hsl(38 92% 50%)",
  destructive: "hsl(0 84% 60%)",
  muted: "hsl(var(--muted-foreground))",
  primary: "hsl(var(--primary))",
};

const tooltipStyle = {
  contentStyle: {
    backgroundColor: "hsl(var(--card))",
    border: "1px solid hsl(var(--border))",
    borderRadius: "8px",
    fontSize: "12px",
    color: "hsl(var(--foreground))",
  },
};

export function MonthlyCommissionChart() {
  const { commissions } = useCAPartner();

  const data = useMemo(() => {
    const months: Record<string, { paid: number; approved: number; pending: number }> = {};
    commissions.forEach((c) => {
      if (!months[c.month]) months[c.month] = { paid: 0, approved: 0, pending: 0 };
      months[c.month][c.status === "paid" ? "paid" : c.status === "approved" ? "approved" : "pending"] += c.commissionAmount;
    });
    return Object.entries(months)
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([month, vals]) => ({
        month: new Date(month + "-01").toLocaleDateString("en-IN", { month: "short", year: "2-digit" }),
        ...vals,
        total: vals.paid + vals.approved + vals.pending,
      }));
  }, [commissions]);

  return (
    <div className="p-5 rounded-lg bg-card border border-border shadow-card">
      <h3 className="text-sm font-semibold text-foreground mb-4">Monthly Commission Earnings</h3>
      <ResponsiveContainer width="100%" height={240}>
        <BarChart data={data} barGap={2}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
          <XAxis dataKey="month" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
          <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => `₹${v}`} />
          <Tooltip {...tooltipStyle} formatter={(value: number) => `₹${value}`} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
          <Bar dataKey="paid" name="Paid" fill={CHART_COLORS.success} radius={[3, 3, 0, 0]} stackId="a" />
          <Bar dataKey="approved" name="Approved" fill={CHART_COLORS.accent} radius={[0, 0, 0, 0]} stackId="a" />
          <Bar dataKey="pending" name="Pending" fill={CHART_COLORS.warning} radius={[3, 3, 0, 0]} stackId="a" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

export function ClientGrowthChart() {
  const { clients } = useCAPartner();

  const data = useMemo(() => {
    const sorted = [...clients].sort((a, b) => new Date(a.onboardedAt).getTime() - new Date(b.onboardedAt).getTime());
    let cumulative = 0;
    const monthly: Record<string, number> = {};
    sorted.forEach((c) => {
      const m = c.onboardedAt.slice(0, 7);
      cumulative++;
      monthly[m] = cumulative;
    });
    return Object.entries(monthly).map(([month, count]) => ({
      month: new Date(month + "-01").toLocaleDateString("en-IN", { month: "short", year: "2-digit" }),
      clients: count,
    }));
  }, [clients]);

  return (
    <div className="p-5 rounded-lg bg-card border border-border shadow-card">
      <h3 className="text-sm font-semibold text-foreground mb-4">Client Growth</h3>
      <ResponsiveContainer width="100%" height={240}>
        <AreaChart data={data}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
          <XAxis dataKey="month" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} />
          <YAxis tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} allowDecimals={false} />
          <Tooltip {...tooltipStyle} />
          <defs>
            <linearGradient id="clientGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={CHART_COLORS.accent} stopOpacity={0.3} />
              <stop offset="95%" stopColor={CHART_COLORS.accent} stopOpacity={0} />
            </linearGradient>
          </defs>
          <Area type="monotone" dataKey="clients" name="Total Clients" stroke={CHART_COLORS.accent} fill="url(#clientGrad)" strokeWidth={2} />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

export function PlanDistributionChart() {
  const { clients } = useCAPartner();

  const data = useMemo(() => {
    const counts: Record<string, number> = { starter: 0, professional: 0, enterprise: 0 };
    clients.filter(c => c.status === "active").forEach(c => counts[c.plan]++);
    return [
      { name: "Starter", value: counts.starter, color: CHART_COLORS.muted },
      { name: "Professional", value: counts.professional, color: CHART_COLORS.accent },
      { name: "Enterprise", value: counts.enterprise, color: CHART_COLORS.success },
    ].filter(d => d.value > 0);
  }, [clients]);

  return (
    <div className="p-5 rounded-lg bg-card border border-border shadow-card">
      <h3 className="text-sm font-semibold text-foreground mb-4">Active Clients by Plan</h3>
      <ResponsiveContainer width="100%" height={200}>
        <PieChart>
          <Pie data={data} cx="50%" cy="50%" innerRadius={50} outerRadius={80} dataKey="value" paddingAngle={4} strokeWidth={0}>
            {data.map((entry, i) => (
              <Cell key={i} fill={entry.color} />
            ))}
          </Pie>
          <Tooltip {...tooltipStyle} />
          <Legend wrapperStyle={{ fontSize: 11 }} />
        </PieChart>
      </ResponsiveContainer>
    </div>
  );
}

export function RevenueContributionChart() {
  const { clients } = useCAPartner();

  const data = useMemo(() => {
    const revenue: Record<string, number> = { starter: 0, professional: 0, enterprise: 0 };
    clients.filter(c => c.status === "active").forEach(c => {
      revenue[c.plan] += c.monthlyAmount * c.commissionRate;
    });
    return [
      { plan: "Starter", commission: Math.round(revenue.starter) },
      { plan: "Professional", commission: Math.round(revenue.professional) },
      { plan: "Enterprise", commission: Math.round(revenue.enterprise) },
    ];
  }, [clients]);

  return (
    <div className="p-5 rounded-lg bg-card border border-border shadow-card">
      <h3 className="text-sm font-semibold text-foreground mb-4">Monthly Commission by Plan</h3>
      <ResponsiveContainer width="100%" height={200}>
        <BarChart data={data} layout="vertical" barSize={20}>
          <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" />
          <XAxis type="number" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} tickFormatter={(v) => `₹${v}`} />
          <YAxis type="category" dataKey="plan" tick={{ fontSize: 11, fill: "hsl(var(--muted-foreground))" }} width={90} />
          <Tooltip {...tooltipStyle} formatter={(value: number) => `₹${value}/mo`} />
          <Bar dataKey="commission" name="Commission" fill={CHART_COLORS.accent} radius={[0, 4, 4, 0]} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}
